import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Dialog, IconButton, Typography } from "@mui/material";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

import useHelps from "../../hooks/useHelps";
import { helpPageLabel, matchHelpPage, youtubeEmbedUrl, youtubeId } from "../../helpers/helpPages";

const CACHE_MS = 5 * 60 * 1000;
const PEEK_MS = 3500;
let cache = { at: 0, items: [] };

export const invalidateHelpsCache = () => {
  cache = { at: 0, items: [] };
};

const useStyles = makeStyles()((theme) => {
  const border = `1px solid ${theme.palette.divider}`;
  const primary = theme.palette.primary.main;

  return {
    trigger: {
      position: "fixed",
      right: 0,
      bottom: 150,
      zIndex: theme.zIndex.speedDial,
      display: "flex",
      alignItems: "center",
      gap: 12,
      height: 40,
      padding: "0 16px 0 9px",
      border: 0,
      borderRadius: "20px 0 0 20px",
      color: theme.palette.primary.contrastText,
      backgroundColor: primary,
      boxShadow: theme.shadows[6],
      font: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
      cursor: "pointer",
      opacity: 0.8,
      transform: "translateX(calc(100% - 36px))",
      transition: "transform .3s ease, opacity .3s ease",
      "&:hover, &:focus-visible": {
        transform: "translateX(0)",
        opacity: 1,
        outline: "none",
      },
    },
    triggerOpen: {
      transform: "translateX(0)",
      opacity: 1,
    },
    triggerIcon: {
      position: "relative",
      display: "flex",
      flexShrink: 0,
    },
    triggerBadge: {
      position: "absolute",
      top: -7,
      right: -8,
      minWidth: 15,
      height: 15,
      padding: "0 4px",
      borderRadius: 8,
      fontSize: 9.5,
      fontWeight: 700,
      lineHeight: "15px",
      textAlign: "center",
      color: primary,
      backgroundColor: theme.palette.common.white,
    },
    paper: {
      borderRadius: 16,
      overflow: "hidden",
      backgroundImage: "none",
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(2.5, 3, 2),
      borderBottom: border,
    },
    overline: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: primary,
    },
    title: {
      fontSize: 19,
      fontWeight: 700,
      lineHeight: 1.3,
      marginTop: 4,
    },
    counter: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    body: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: theme.spacing(2.5),
      padding: theme.spacing(3),
    },
    bodyWithList: {
      gridTemplateColumns: "minmax(0, 1fr) 280px",
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "minmax(0, 1fr)",
      },
    },
    player: {
      position: "relative",
      width: "100%",
      paddingTop: "56.25%",
      borderRadius: 12,
      overflow: "hidden",
      background: "#000",
      boxShadow: theme.shadows[3],
      "& iframe": {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
      },
    },
    description: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      borderRadius: 10,
      border,
      background: alpha(theme.palette.text.primary, 0.03),
      color: theme.palette.text.secondary,
      fontSize: 14,
      lineHeight: 1.6,
      whiteSpace: "pre-line",
    },
    playlist: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      alignSelf: "start",
      maxHeight: 520,
      overflowY: "auto",
      paddingRight: 2,
      ...theme.scrollbarStyles,
    },
    playlistTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      marginBottom: 2,
    },
    item: {
      display: "grid",
      gridTemplateColumns: "112px minmax(0, 1fr)",
      gap: theme.spacing(1.25),
      alignItems: "center",
      padding: theme.spacing(0.75),
      borderRadius: 10,
      border: "1px solid transparent",
      background: "transparent",
      color: theme.palette.text.primary,
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      transition: "background-color .15s, border-color .15s",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    itemActive: {
      borderColor: alpha(primary, 0.5),
      backgroundColor: alpha(primary, 0.08),
      "&:hover": {
        backgroundColor: alpha(primary, 0.12),
      },
    },
    thumb: {
      position: "relative",
      display: "block",
      width: 112,
      aspectRatio: "16 / 9",
      borderRadius: 8,
      overflow: "hidden",
      background: alpha(theme.palette.text.primary, 0.08),
      "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      },
    },
    thumbNumber: {
      position: "absolute",
      left: 4,
      top: 4,
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      borderRadius: 9,
      background: "rgba(0,0,0,0.7)",
      color: "#fff",
      fontSize: 10.5,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    itemText: {
      display: "block",
      minWidth: 0,
    },
    itemTitle: {
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.35,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    itemState: {
      display: "block",
      fontSize: 11.5,
      marginTop: 2,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    itemStateActive: {
      color: primary,
      fontWeight: 600,
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 3),
      borderTop: border,
    },
    navButton: {
      textTransform: "none",
      fontWeight: 600,
    },
  };
});

const Miniatura = ({ video, numero, className, numberClass }) => {
  const id = youtubeId(video);
  return (
    <span className={className}>
      {id && <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt="" loading="lazy" />}
      <span className={numberClass}>{numero}</span>
    </span>
  );
};

const PageHelp = () => {
  const { classes, cx } = useStyles();
  const { pathname } = useLocation();
  const { list } = useHelps();

  const [helps, setHelps] = useState(cache.items);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (Date.now() - cache.at < CACHE_MS) {
      setHelps(cache.items);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const data = await list();
        cache = { at: Date.now(), items: Array.isArray(data) ? data : [] };
        if (!cancelado) setHelps(cache.items);
      } catch {
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const pagina = matchHelpPage(pathname);

  const deEstaPagina = useMemo(
    () => (pagina ? helps.filter((h) => h.page === pagina && h.video) : []),
    [helps, pagina]
  );

  useEffect(() => {
    setOpen(false);
    setSelected(0);
  }, [pathname]);

  const [peek, setPeek] = useState(false);
  const hayAyuda = deEstaPagina.length > 0;

  useEffect(() => {
    if (!hayAyuda) return undefined;
    setPeek(true);
    const timer = setTimeout(() => setPeek(false), PEEK_MS);
    return () => clearTimeout(timer);
  }, [pagina, hayAyuda]);

  if (!deEstaPagina.length) return null;

  const indice = Math.min(selected, deEstaPagina.length - 1);
  const actual = deEstaPagina[indice];
  const varios = deEstaPagina.length > 1;

  return (
    <>
      <button
        type="button"
        className={cx(classes.trigger, { [classes.triggerOpen]: peek }, "no-imprimir")}
        onClick={() => setOpen(true)}
        aria-label="Ver ayuda"
      >
        <span className={classes.triggerIcon}>
          <OndemandVideoOutlinedIcon fontSize="small" />
          {varios && <span className={classes.triggerBadge}>{deEstaPagina.length}</span>}
        </span>
        Ver ayuda
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth={varios ? "lg" : "md"}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.header}>
          <div>
            <div className={classes.overline}>
              <OndemandVideoOutlinedIcon sx={{ fontSize: 15 }} />
              Ayuda · {helpPageLabel(pagina)}
            </div>
            <Typography className={classes.title}>{actual.title}</Typography>
            {varios && (
              <div className={classes.counter}>
                Video {indice + 1} de {deEstaPagina.length}
              </div>
            )}
          </div>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={cx(classes.body, { [classes.bodyWithList]: varios })}>
          <div>
            <div className={classes.player}>
              <iframe
                key={actual.id}
                src={youtubeEmbedUrl(actual.video)}
                title={actual.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {actual.description && <div className={classes.description}>{actual.description}</div>}
          </div>

          {varios && (
            <div className={classes.playlist}>
              <div className={classes.playlistTitle}>En esta página</div>
              {deEstaPagina.map((h, i) => (
                <button
                  key={h.id}
                  type="button"
                  className={cx(classes.item, { [classes.itemActive]: i === indice })}
                  onClick={() => setSelected(i)}
                >
                  <Miniatura video={h.video} numero={i + 1} className={classes.thumb} numberClass={classes.thumbNumber} />
                  <span className={classes.itemText}>
                    <span className={classes.itemTitle}>{h.title}</span>
                    <span className={cx(classes.itemState, { [classes.itemStateActive]: i === indice })}>
                      {i === indice ? "Reproduciendo" : h.description || " "}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {varios && (
          <div className={classes.footer}>
            <Button
              className={classes.navButton}
              startIcon={<ChevronLeftIcon />}
              disabled={indice === 0}
              onClick={() => setSelected(indice - 1)}
            >
              Anterior
            </Button>
            <Button
              className={classes.navButton}
              variant="contained"
              disableElevation
              endIcon={<ChevronRightIcon />}
              disabled={indice === deEstaPagina.length - 1}
              onClick={() => setSelected(indice + 1)}
            >
              Siguiente
            </Button>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default PageHelp;
